import { useTranslation } from 'react-i18next';
import { useContext } from 'react';
import { cn } from '@/utils/cn';
import { DeviceFrameDensityContext, PreviewFluidContext } from '../components/deviceFrameStyle';
import { Icon } from '@/components/ui/Icon';
import { ICONS } from '@/constants/icons';
import { PlatformPreviewProps } from '../types';
import { PLATFORM_CONFIG, truncateForPlatform } from '../platformConfig';
import { PreviewAvatar } from '../components/PreviewAvatar';
import { EmptyState } from '../components/EmptyState';
import { usePlaceholderImage } from '@/hooks/usePlaceholderImage';
import { selectPinterestMedia } from '../previewMedia';

export const PinterestPreview = ({ caption, media, channel }: PlatformPreviewProps) => {
  const { t } = useTranslation('common');
  const placeholder = usePlaceholderImage();
  const slim = useContext(DeviceFrameDensityContext) === 'slim';
  const fluid = useContext(PreviewFluidContext);
  const config = PLATFORM_CONFIG.pinterest;
  const displayCaption = truncateForPlatform(caption, 'pinterest');
  const isEmpty = !caption && media.length === 0;

  const displayName = channel?.displayName ?? '';
  const options = channel?.options;
  const pinTitle = options?.pinterest?.title;
  const pinDescription = options?.pinterest?.description;
  const firstLine = displayCaption.split('\n').find((l) => l.trim())?.trim() ?? '';
  const pinLink = options?.pinterest?.link;

  const selected = selectPinterestMedia(media);

  if (isEmpty) return <EmptyState />;

  return (
    <div className={cn('bg-white rounded-2xl w-full min-w-0 mx-auto overflow-hidden border border-gray-100', fluid ? 'max-w-none' : 'max-w-[320px]', slim ? 'shadow-sm' : 'shadow-md')}>
      {/* Pin Image: the image's own proportions (tall pins are cropped like
          Pinterest does), never padded with blurred bands */}
      {selected.url ? (
        <div className="relative bg-gray-100">
          <img
            src={selected.url}
            alt={t('preview_pinterest_pin_alt')}
            className="block h-auto max-h-[460px] min-h-[120px] w-full object-cover"
            onError={(e) => {
              e.currentTarget.src = placeholder;
            }}
          />
          {selected.kind === 'video-pin' && (
            <span className="absolute bottom-2.5 left-2.5 bg-black/60 text-white text-xs px-2 py-0.5 rounded-full">
              {t('preview_pinterest_video_pin')}
            </span>
          )}
        </div>
      ) : (
        <div className="aspect-[2/3] bg-gray-100 flex items-center justify-center">
          <Icon name={ICONS.IMAGE} size="xl" color="text-gray-300" />
        </div>
      )}

      {/* Pin Info */}
      <div className="px-3 pt-2.5 pb-3">
        {/* Pin title: only a real one (from options) is styled as a title;
            without it, a plain caption excerpt so nothing looks configured */}
        {pinTitle ? (
          <p className="text-sm text-gray-900 font-semibold line-clamp-2">{pinTitle}</p>
        ) : firstLine ? (
          <p className="text-xs text-gray-600 line-clamp-2">{firstLine}</p>
        ) : null}

        {/* Pin description */}
        {pinDescription && (
          <p className="text-xs text-gray-600 mt-1 line-clamp-2">{pinDescription}</p>
        )}

        {/* Link */}
        {pinLink && (
          <p className="text-xs text-gray-400 mt-1 truncate flex items-center gap-1">
            <Icon name={ICONS.LINK} size="xs" color="text-gray-400" />
            {pinLink.replace(/^https?:\/\//, '').split('/')[0]}
          </p>
        )}

        <div className="flex items-center gap-2 mt-2.5">
          <PreviewAvatar colorClass={config.avatarColor} size="sm" avatarUrl={channel?.avatarUrl} initials={channel?.initials} />
          <span className="text-xs text-gray-600">{displayName}</span>
        </div>
      </div>
    </div>
  );
};
